import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Card,
  GlobalStyles,
  Alert,
  Skeleton,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useLegalDocument } from '@/hooks/useLegalDocuments';
import { useDateFormatter } from '@/hooks/useDateFormatter';

const TermsAndConditionsPage = () => {
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const { data: document, isLoading, error } = useLegalDocument('terms_and_conditions');

  return (
    <>
      <GlobalStyles
        styles={{
          body: {
            margin: 0,
            padding: 0,
          },
          html: {
            margin: 0,
            padding: 0,
          },
        }}
      />
      <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#2f4255',
        position: 'relative',
        pt: 4,
        pb: 0,
        margin: 0,
        '& .MuiContainer-root': {
          paddingBottom: 0,
        },
      }}
    >
      {/* Floating particles */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100vh',
          zIndex: 1,
        }}
      >
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -20, 0],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
            style={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: '3px',
              height: '3px',
              borderRadius: '50%',
              backgroundColor: '#FF6B35',
            }}
          />
        ))}
      </Box>

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, pb: 0 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Card
            sx={{
              p: { xs: 4, md: 6 },
              pb: { xs: 2, md: 3 },
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              mb: 0,
            }}
          >
            {/* Header */}
            <Stack spacing={3} alignItems="center" textAlign="center" sx={{ mb: 4 }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: '#4a91e2',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(74, 145, 226, 0.3)',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                    }}
                  >
                    AH
                  </Typography>
                </Box>
              </motion.div>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#1A1A1A',
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                }}
              >
                {isLoading ? (
                  <Skeleton width={300} />
                ) : (
                  document?.title || 'Terms and Conditions'
                )}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  fontSize: '1.1rem',
                }}
              >
                {isLoading ? (
                  <Skeleton width={200} />
                ) : document?.published_at ? (
                  `Last updated: ${formatDate(document.published_at, 'dd/MM/yyyy')}`
                ) : (
                  `Version: ${document?.version || '1.0'}`
                )}
              </Typography>
            </Stack>

            {/* Content */}
            <Box sx={{ textAlign: 'left', mb: 5 }}>
              {isLoading ? (
                <Stack spacing={3}>
                  <Skeleton variant="rectangular" height={80} />
                  <Skeleton variant="rectangular" height={120} />
                  <Skeleton variant="rectangular" height={100} />
                  <Skeleton variant="rectangular" height={80} />
                </Stack>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {t('app.legalDocuments.loadError', {
                    defaultValue: 'Erro ao carregar documento. Por favor, tente novamente mais tarde.',
                  })}
                </Alert>
              ) : !document ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  {t('app.legalDocuments.noActiveDocument', {
                    defaultValue: 'Nenhum documento ativo disponível no momento.',
                  })}
                </Alert>
              ) : (
                <Box
                  sx={{
                    '& h2': {
                      fontWeight: 600,
                      color: '#1A1A1A',
                      fontSize: '1.5rem',
                      mb: 2.5,
                      mt: 3,
                    },
                    '& h3': {
                      fontWeight: 600,
                      color: '#1A1A1A',
                      fontSize: '1.25rem',
                      mb: 2,
                      mt: 2.5,
                    },
                    '& p': {
                      color: '#666',
                      lineHeight: 1.7,
                      fontSize: '1.1rem',
                      mb: 2,
                    },
                    '& ul, & ol': {
                      ml: 2,
                      mb: 2,
                    },
                    '& li': {
                      color: '#666',
                      lineHeight: 1.7,
                      fontSize: '1.05rem',
                      mb: 1,
                    },
                    '& a': {
                      color: '#4a91e2',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: document.content }}
                />
              )}
            </Box>

            {/* Back to Login Link */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 0 }}>
              <Button
                component={RouterLink}
                to="/login"
                variant="text"
                startIcon={<ArrowLeft size={18} />}
                sx={{
                  color: '#4a91e2',
                  fontSize: '1rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(74, 145, 226, 0.05)',
                    textDecoration: 'underline',
                  },
                }}
              >
                Back to Login
              </Button>
            </Box>
          </Card>
        </motion.div>
      </Container>
    </Box>
    </>
  );
};

export default TermsAndConditionsPage;